import type { AssetInfo } from 'actions/asset';
import type { AccidentInfo } from 'actions/accident';
import type { SurveyInfo } from 'actions/survey';
import type { FwpInfo, SchemeInfo, TreatmentInfo } from 'actions/scheme';
import type { PerformanceInfo } from 'actions/performance';
import type { SectionInfo } from 'actions/network';

/* Legend / map click categories handled by attribute resolvers. */
export type AttributeDetailCategory =
  | 'asset'
  | 'accident'
  | 'survey'
  | 'inspection'
  | 'scheme'
  | 'candidate schemes'
  | 'programmed scheme'
  | 'treatment'
  | 'performance'
  | 'network'
  | 'network-fallback';

/** Categories resolved via {@link resolveByCategory} (excludes implicit network fallback). */
export type ResolvableAttributeDetailCategory = Exclude<
  AttributeDetailCategory,
  'network-fallback'
>;

/** Asset API payload includes tab flags and id beyond {@link AssetInfo} fields. */
export interface AssetAttributeModalDetails extends AssetInfo {
  readonly id?: number;
  readonly attributes?: boolean;
  readonly elements?: boolean;
  readonly segmentable?: boolean;
}

export type AttributeModalDetails =
  | AssetAttributeModalDetails
  | AccidentInfo
  | SurveyInfo
  | SchemeInfo
  | FwpInfo
  | TreatmentInfo
  | PerformanceInfo
  | SectionInfo;

export type ResolvedAttributeDetails =
  | {
      readonly category: 'asset';
      readonly details: AssetAttributeModalDetails;
    }
  | {
      readonly category: 'accident';
      readonly details: AccidentInfo;
    }
  | {
      readonly category: 'survey';
      readonly details: SurveyInfo;
    }
  | {
      readonly category: 'inspection';
      readonly details: SurveyInfo;
    }
  | {
      readonly category: 'scheme';
      readonly details: SchemeInfo;
    }
  | {
      readonly category: 'candidate schemes';
      readonly details: FwpInfo;
    }
  | {
      readonly category: 'programmed scheme';
      readonly details: SchemeInfo;
    }
  | {
      readonly category: 'treatment';
      readonly details: TreatmentInfo;
    }
  | {
      readonly category: 'performance';
      readonly details: PerformanceInfo;
    }
  | {
      readonly category: 'network';
      readonly details: SectionInfo;
    }
  | {
      readonly category: 'network-fallback';
      readonly details: SectionInfo;
    };

export function isAttributeDetailCategory(
  value: string | undefined,
): value is ResolvableAttributeDetailCategory {
  return (
    value === 'asset' ||
    value === 'accident' ||
    value === 'survey' ||
    value === 'inspection' ||
    value === 'scheme' ||
    value === 'candidate schemes' ||
    value === 'programmed scheme' ||
    value === 'treatment' ||
    value === 'performance' ||
    value === 'network'
  );
}

export type ModalDetailsState =
  | AttributeModalDetails
  | Record<string, never>
  | Record<string, string>;

export function isAssetAttributeModalDetails(
  details: ModalDetailsState | undefined,
): details is AssetAttributeModalDetails {
  if (details == null || typeof details !== 'object') {
    return false;
  }
  const record = details as Record<string, unknown>;
  return (
    typeof record.assetTypeCode === 'string' ||
    typeof record.number === 'string' ||
    typeof record.attributes === 'boolean' ||
    typeof record.elements === 'boolean'
  );
}

export interface ModalDetailDisplayEntry {
  readonly key: string;
  readonly value: unknown;
}

export function getAssetModalId(
  details: ModalDetailsState,
): number | undefined {
  if ('id' in details && typeof details.id === 'number') {
    return details.id;
  }
  return undefined;
}

export function getModalGeometry(
  details: ModalDetailsState,
): string | undefined {
  if ('geometry' in details && typeof details.geometry === 'string') {
    return details.geometry;
  }
  return undefined;
}

export function getModalDetailDisplayEntries(
  details: ModalDetailsState,
): ReadonlyArray<ModalDetailDisplayEntry> {
  if (!details || Object.keys(details).length === 0) {
    return [];
  }

  const hidden = new Set(['geometry', 'attributes', 'elements', 'segmentable']);
  const record = details as Record<string, unknown>;

  return Object.keys(record)
    .filter((key) => !hidden.has(key))
    .map((key) => ({ key, value: record[key] }));
}
